import {
  DASHBOARD_DATE_SELECTION_KIND,
  type DashboardDateSelection,
  type DashboardPeriod,
  type SearchRankingKeywordDetail,
  type SearchRankingProductHistory,
} from "@nova-ai/shared"

/** The shape these helpers need from a history entry. Both the overview's
 *  one-per-day series and the keyword detail's one-per-capture series satisfy
 *  it, so range and summary logic stay shared — only the input granularity
 *  differs. */
type RankPoint = {
  day: string
  measured: boolean
  bestOrganicRank: number | null
  bestSponsoredRank: number | null
}

export type SearchRankingDateRange = {
  from: string
  to: string
}

export const SEARCH_RANKING_DATE_PERIODS = [
  "7d",
  "14d",
  "30d",
] as const satisfies readonly DashboardPeriod[]

export type ProductChartSeries = {
  rankProductKey: string
  name: string
  url: string
  matchedSku: string | null
  values: {
    runId: string
    capturedAt: string
    day: string
    measured: boolean
    rank: number | null
  }[]
}

export type SelectableProductSeries = ProductChartSeries & {
  bestRank: number
  currentRank: number | null
  periodMovement: ProductPeriodMovement
}

export type ProductPeriodMovement =
  | { kind: "change"; delta: number }
  | { kind: "entered" }
  | { kind: "left" }
  | { kind: "off-page" }
  | { kind: "insufficient" }

/** Which of the two rankings a chart or summary is describing. Organic and paid
 *  are never plotted together: 60 organic results and 12 auctioned slots do not
 *  share a scale, so one axis would make "#5" mean two different things. */
export const RANK_CHANNELS = [
  { value: "organic", label: "Organic" },
  { value: "sponsored", label: "Sponsored" },
] as const

/** Column copy shared by the tracker table and the product page's slice of it.
 *  Both render the same two ranks, and a rank column that explains itself one way
 *  on one page and another way on the other is how the vocabulary drifts. */
export const ORGANIC_RANK_HINT =
  "Our best unpaid position on the first results page, from the latest capture. #1 is the first unpaid result."

export const SPONSORED_RANK_HINT =
  "Our best paid position on the first results page, from the latest capture. #1 is the first paid slot. Independent of Organic # — 60 unpaid results and a dozen auctioned slots are two different rankings."

export type RankChannel = (typeof RANK_CHANNELS)[number]["value"]

const channelRankOf = (point: RankPoint, channel: RankChannel): number | null =>
  channel === "sponsored" ? point.bestSponsoredRank : point.bestOrganicRank

type RankHistorySummary = {
  bestRank: number | null
  currentRank: number | null
  rankChange: number | null
  /** Distinct days that carry at least one usable capture — counting entries
   *  would count captures, and the detail series holds several per day. */
  measuredDays: number
}

const DAY_MS = 24 * 60 * 60 * 1000

function availableHistoryRange(history: RankPoint[]): SearchRankingDateRange | null {
  const first = history.at(0)
  const lastMeasured = history.findLast((day) => day.measured)
  const last = lastMeasured ?? history.at(-1)
  return first && last ? { from: first.day, to: last.day } : null
}

export function dateRangeForSelection(
  history: RankPoint[],
  selection: DashboardDateSelection,
  fixedAvailableRange?: SearchRankingDateRange
): SearchRankingDateRange | null {
  const available = fixedAvailableRange ?? availableHistoryRange(history)
  if (!available) {
    return null
  }
  switch (selection.kind) {
    case DASHBOARD_DATE_SELECTION_KIND.rolling: {
      const lookback = rankingLookbackDays(selection.period)
      const desiredFrom = shiftCalendarDay(available.to, -lookback)
      return { from: maxDay(desiredFrom, available.from), to: available.to }
    }
    case DASHBOARD_DATE_SELECTION_KIND.month: {
      const monthFrom = `${selection.month}-01`
      const [yearRaw, monthRaw] = selection.month.split("-")
      const monthTo = new Date(Date.UTC(Number(yearRaw), Number(monthRaw), 0))
        .toISOString()
        .slice(0, 10)
      return {
        from: maxDay(monthFrom, available.from),
        to: minDay(monthTo, available.to),
      }
    }
    case DASHBOARD_DATE_SELECTION_KIND.custom:
      return {
        from: maxDay(selection.from, available.from),
        to: minDay(selection.to, available.to),
      }
  }
}

/** Keep a snapshot-selected product eligible without silently replacing it.
 * The selected capture already carries the marketplace-local day computed by
 * the API, so extending the picker uses the same calendar semantics as every
 * history bucket. Existing picker modes stay intact while they include the day;
 * an out-of-range click becomes an explicit custom union. */
export function dateSelectionIncludingDay(
  history: RankPoint[],
  selection: DashboardDateSelection,
  availableRange: SearchRankingDateRange,
  day: string
): DashboardDateSelection {
  const activeRange = dateRangeForSelection(history, selection, availableRange)
  if (activeRange && day >= activeRange.from && day <= activeRange.to) {
    return selection
  }
  const from = maxDay(
    activeRange === null ? day : minDay(activeRange.from, day),
    availableRange.from
  )
  const to = minDay(activeRange === null ? day : maxDay(activeRange.to, day), availableRange.to)
  return {
    kind: DASHBOARD_DATE_SELECTION_KIND.custom,
    from,
    to,
  }
}

/** Generic in the point type so a filtered detail series keeps its
 *  `capturedAt` — the chart plots by it. */
export function filterRankHistory<Point extends RankPoint>(
  history: Point[],
  range: SearchRankingDateRange
): Point[] {
  return history.filter((point) => point.day >= range.from && point.day <= range.to)
}

/** Collapse a per-capture series to one point per day, keeping the day's **last
 *  usable** capture — the same rule the overview's daily series uses, so the two
 *  never disagree about a day. A day with no usable capture stays in place as an
 *  unmeasured gap rather than vanishing, because "we could not measure" is its
 *  own outcome and must not close the line across it.
 *
 *  Used for multi-day ranges: eight points a day is the signal on a 24h view and
 *  noise on a 30d one. */
export function collapseToDailyPoints<Point extends RankPoint>(points: Point[]): Point[] {
  const byDay = new Map<string, Point>()
  for (const point of points) {
    const kept = byDay.get(point.day)
    // Ascending input, so a later point of the same day wins — but a measured
    // capture always beats an unmeasured one, whatever the order.
    if (kept === undefined || point.measured || !kept.measured) {
      byDay.set(point.day, point)
    }
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day))
}

/** How many distinct days a series touches. */
export function distinctDayCount(points: RankPoint[]): number {
  return new Set(points.map((point) => point.day)).size
}

/** Per-capture for a single calendar day, one point per day for anything wider.
 *  Read off the selected range, never inferred from the points it happens to
 *  contain: a range can legitimately hold one day's captures and still be a
 *  week wide (a week with one working day), and the reverse would silently
 *  collapse the day view. */
export function rankChartGranularity(range: SearchRankingDateRange | null): "capture" | "day" {
  return range !== null && range.from === range.to ? "capture" : "day"
}

export function rankHistorySummary(
  history: RankPoint[],
  channel: RankChannel = "organic"
): RankHistorySummary {
  const ranked = history
    .filter((day) => day.measured)
    .map((day) => ({ day, rank: channelRankOf(day, channel) }))
    .filter((entry): entry is { day: RankPoint; rank: number } => entry.rank !== null)
  const first = ranked.at(0)
  const current = ranked.at(-1)
  return {
    bestRank: ranked.length === 0 ? null : Math.min(...ranked.map((entry) => entry.rank)),
    currentRank: current?.rank ?? null,
    rankChange: first && current && first !== current ? first.rank - current.rank : null,
    measuredDays: new Set(history.filter((point) => point.measured).map((point) => point.day)).size,
  }
}

export function buildProductChartSeries(
  history: SearchRankingKeywordDetail["history"],
  products: SearchRankingProductHistory[],
  channel: RankChannel,
  granularity: "capture" | "day"
): ProductChartSeries[] {
  const anchors = granularity === "capture" ? history : collapseToDailyPoints(history)
  return products.map((product) => {
    const pointByRun = new Map(product.points.map((point) => [point.runId, point]))
    return {
      rankProductKey: product.rankProductKey,
      name: product.name,
      url: product.url,
      matchedSku: product.matchedSku,
      values: anchors.map((anchor) => {
        const productPoint = pointByRun.get(anchor.runId)
        let rank: number | null = null
        if (anchor.measured && productPoint) {
          rank = channel === "sponsored" ? productPoint.sponsoredRank : productPoint.organicRank
        }
        return {
          runId: anchor.runId,
          capturedAt: anchor.capturedAt,
          day: anchor.day,
          measured: anchor.measured,
          rank,
        }
      }),
    }
  })
}

/** Products that have at least one measured rank in the active channel and
 * range. Current rank reads the latest measured capture, so a product that was
 * ranked earlier but has left page one stays selectable with an "off page"
 * state. Sorting makes the default stable: best current rank, then historical
 * best, then product identity. */
export function selectableProductSeries(
  history: SearchRankingKeywordDetail["history"],
  products: SearchRankingProductHistory[],
  channel: RankChannel,
  granularity: "capture" | "day"
): SelectableProductSeries[] {
  return buildProductChartSeries(history, products, channel, granularity)
    .flatMap((product) => {
      const ranked = product.values.filter(
        (point): point is typeof point & { rank: number } => point.measured && point.rank !== null
      )
      if (ranked.length === 0) {
        return []
      }
      const current = product.values.findLast((point) => point.measured)
      return [
        {
          ...product,
          bestRank: Math.min(...ranked.map((point) => point.rank)),
          currentRank: current?.rank ?? null,
          periodMovement: productPeriodMovement(product.values),
        },
      ]
    })
    .sort(
      (a, b) =>
        (a.currentRank ?? Number.MAX_SAFE_INTEGER) - (b.currentRank ?? Number.MAX_SAFE_INTEGER) ||
        a.bestRank - b.bestRank ||
        a.name.localeCompare(b.name) ||
        a.rankProductKey.localeCompare(b.rankProductKey)
    )
}

/** Movement between the first and last measured capture in the selected
 * period. Off-page is an observed state, not a missing value: leaving or
 * entering page one must stay visible even though one endpoint has no rank. */
function productPeriodMovement(values: ProductChartSeries["values"]): ProductPeriodMovement {
  const measured = values.filter((point) => point.measured)
  const first = measured.at(0)
  const current = measured.at(-1)
  if (!first || !current || first === current) {
    return { kind: "insufficient" }
  }
  if (first.rank === null && current.rank === null) {
    return { kind: "off-page" }
  }
  if (first.rank === null) {
    return { kind: "entered" }
  }
  if (current.rank === null) {
    return { kind: "left" }
  }
  return { kind: "change", delta: first.rank - current.rank }
}

function rankingLookbackDays(period: DashboardPeriod): number {
  if (period === "7d") {
    return 6
  }
  if (period === "14d") {
    return 13
  }
  return 29
}

/** How the organic position moved between the two most recent measured days.
 *  Losing the first page entirely is its own outcome: comparing only days that
 *  *had* a rank would render the disappearance as "no change", which is the one
 *  signal the tracker exists to surface. */
type OrganicMove = { kind: "change"; delta: number } | { kind: "lost" } | { kind: "entered" }

export function latestOrganicMove(history: RankPoint[]): OrganicMove | null {
  const measured = history.filter((day) => day.measured)
  const last = measured.at(-1)
  const previous = measured.at(-2)
  if (!last || !previous) {
    return null
  }
  if (previous.bestOrganicRank === null && last.bestOrganicRank === null) {
    return null
  }
  if (last.bestOrganicRank === null) {
    return { kind: "lost" }
  }
  if (previous.bestOrganicRank === null) {
    return { kind: "entered" }
  }
  return { kind: "change", delta: previous.bestOrganicRank - last.bestOrganicRank }
}

export function latestRankChange(history: RankPoint[]): number | null {
  const move = latestOrganicMove(history)
  return move?.kind === "change" ? move.delta : null
}

/**
 * Day-over-day move, whatever granularity the caller holds.
 *
 * The overview reads a one-point-per-day series, so its Change column is
 * day-over-day; the keyword detail reads one point per capture, where the same
 * call would compare the last two captures of the day instead. That made the
 * two pages render the same-looking arrow off different comparisons — with
 * three captures a day they disagree routinely. Collapsing first is what makes
 * them one number.
 */
export function latestDailyRankChange(history: RankPoint[]): number | null {
  return latestRankChange(collapseToDailyPoints(history))
}

function shiftCalendarDay(day: string, offsetDays: number): string {
  const timestamp = Date.parse(`${day}T00:00:00Z`)
  return new Date(timestamp + offsetDays * DAY_MS).toISOString().slice(0, 10)
}

const maxDay = (left: string, right: string) => (left > right ? left : right)
const minDay = (left: string, right: string) => (left < right ? left : right)

/** One entry per measured day, holding that day's **last** measured capture — the
 *  same rule the daily series uses, so stepping through days and reading the
 *  trend line agree about what a day's position was. */
export function measuredDays<Point extends RankPoint & { runId: string }>(
  history: Point[]
): Point[] {
  const byDay = new Map<string, Point>()
  for (const point of history) {
    if (point.measured) {
      byDay.set(point.day, point)
    }
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day))
}
